import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { apiFetch } from "@/lib/api";
import { useLang } from "../i18n/LanguageContext";
import { translations } from "../i18n/translations";

/* ─── Constants ─────────────────────────────── */
const GOLD = "#C9A84C";
const NAVY = "#0B308E";

const ALL_COUNTRIES = [
  "Afghanistan", "Albania", "Algeria", "Andorra", "Angola", "Antigua and Barbuda",
  "Argentina", "Armenia", "Australia", "Austria", "Azerbaijan", "Bahamas", "Bahrain",
  "Bangladesh", "Barbados", "Belarus", "Belgium", "Belize", "Benin", "Bhutan",
  "Bolivia", "Bosnia and Herzegovina", "Botswana", "Brazil", "Brunei", "Bulgaria",
  "Burkina Faso", "Burundi", "Cabo Verde", "Cambodia", "Cameroon", "Canada",
  "Central African Republic", "Chad", "Chile", "China", "Colombia", "Comoros",
  "Congo (Congo-Brazzaville)", "Costa Rica", "Croatia", "Cuba", "Cyprus", "Czechia",
  "Denmark", "Djibouti", "Dominica", "Dominican Republic", "Ecuador", "Egypt",
  "El Salvador", "Equatorial Guinea", "Eritrea", "Estonia", "Eswatini", "Ethiopia",
  "Fiji", "Finland", "France", "Gabon", "Gambia", "Georgia", "Germany", "Ghana",
  "Greece", "Grenada", "Guatemala", "Guinea", "Guinea-Bissau", "Guyana", "Haiti",
  "Honduras", "Hungary", "Iceland", "India", "Indonesia", "Iran", "Iraq", "Ireland",
  "Israel", "Italy", "Jamaica", "Japan", "Jordan", "Kazakhstan", "Kenya", "Kiribati",
  "Kuwait", "Kyrgyzstan", "Laos", "Latvia", "Lebanon", "Lesotho", "Liberia", "Libya",
  "Liechtenstein", "Lithuania", "Luxembourg", "Madagascar", "Malawi", "Malaysia",
  "Maldives", "Mali", "Malta", "Marshall Islands", "Mauritania", "Mauritius",
  "Mexico", "Micronesia", "Moldova", "Monaco", "Mongolia", "Montenegro", "Morocco",
  "Mozambique", "Myanmar", "Namibia", "Nauru", "Nepal", "Netherlands", "New Zealand",
  "Nicaragua", "Niger", "Nigeria", "North Korea", "North Macedonia", "Norway", "Oman",
  "Pakistan", "Palau", "Palestine", "Panama", "Papua New Guinea", "Paraguay", "Peru",
  "Philippines", "Poland", "Portugal", "Qatar", "Romania", "Russia", "Rwanda",
  "Saint Kitts and Nevis", "Saint Lucia", "Saint Vincent and the Grenadines", "Samoa",
  "San Marino", "Sao Tome and Principe", "Saudi Arabia", "Senegal", "Serbia",
  "Seychelles", "Sierra Leone", "Singapore", "Slovakia", "Slovenia", "Solomon Islands",
  "Somalia", "South Africa", "South Korea", "South Sudan", "Spain", "Sri Lanka",
  "Sudan", "Suriname", "Sweden", "Switzerland", "Syria", "Taiwan", "Tajikistan",
  "Tanzania", "Thailand", "Timor-Leste", "Togo", "Tonga", "Trinidad and Tobago",
  "Tunisia", "Turkey", "Turkmenistan", "Tuvalu", "Uganda", "Ukraine",
  "United Arab Emirates", "United Kingdom", "United States", "Uruguay", "Uzbekistan",
  "Vanuatu", "Venezuela", "Vietnam", "Yemen", "Zambia", "Zimbabwe",
];

const SPECIALITIES = [
  "Product Development",
  "Information Systems",
  "External Shariah Audit",
  "Training",
  "Risk Management & Internal Controls",
  "Compliance",
  "Marketing & Communication",
  "Others",
];

/* ─── IGC Logo ──────────────────────────────── */
const IGCLogo = () => (
  <div
    style={{
      backgroundColor: NAVY,
      display: "inline-flex",
      alignItems: "center",
      padding: "8px 14px",
    }}
  >
    <span style={{ color: "#fff", fontFamily: '"Times New Roman", Times, serif', fontSize: "1.6rem", fontWeight: "normal", lineHeight: 1, letterSpacing: "0.02em" }}>I</span>
    <span style={{ width: "1px", backgroundColor: "#fff", height: "1.3rem", margin: "0 9px", display: "block", flexShrink: 0 }} />
    <span style={{ color: "#fff", fontFamily: '"Times New Roman", Times, serif', fontSize: "1.6rem", fontWeight: "normal", lineHeight: 1, letterSpacing: "0.02em" }}>G</span>
    <span style={{ width: "1px", backgroundColor: "#fff", height: "1.3rem", margin: "0 9px", display: "block", flexShrink: 0 }} />
    <span style={{ color: "#fff", fontFamily: '"Times New Roman", Times, serif', fontSize: "1.6rem", fontWeight: "normal", lineHeight: 1, letterSpacing: "0.02em" }}>C</span>
  </div>
);

/* ─── Main Component ────────────────────────── */
export default function JoinIGC() {
  const { lang } = useLang();
  const t = translations[lang];
  const navigate = useNavigate();
  const isRtl = lang === "ar";

  const [form, setForm] = useState({
    surname: "",
    name: "",
    email: "",
    country: "",
    speciality: "",
  });
  const [cvFile, setCvFile] = useState<File | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      if (cvFile) fd.append("cv", cvFile);
      const res = await apiFetch("/join", { method: "POST", body: fd, auth: false });
      if (res.ok) {
        setSent(true);
      } else {
        const data = await res.json().catch(() => ({}));
        setError((data as { error?: { message?: string } })?.error?.message || t.join_error_msg);
      }
    } catch {
      setError(t.join_error_msg);
    } finally {
      setSubmitting(false);
    }
  };

  const inputClass =
    "w-full px-4 py-3 text-sm border border-gray-200 focus:outline-none focus:border-primary transition-colors bg-white";
  const labelClass = "block text-xs font-semibold tracking-wide mb-1.5" ;

  return (
    <div
      className="min-h-screen bg-white"
      dir={isRtl ? "rtl" : "ltr"}
      style={{ fontFamily: "Inter, sans-serif" }}
    >
      {/* Header bar */}
      <div style={{ background: NAVY, padding: "12px 24px", display: "flex", alignItems: "center", gap: 16 }}>
        <button onClick={() => navigate("/")} className="text-white/80 hover:text-white transition-colors text-sm flex items-center gap-2">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="15 18 9 12 15 6" />
          </svg>
          Back
        </button>
        <IGCLogo />
      </div>

      {/* Decorative top border */}
      <div style={{ height: 3, background: `linear-gradient(90deg, ${NAVY}, ${GOLD})` }} />

      <div className="container mx-auto px-6 max-w-3xl py-16">
        {/* Page header */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          {/* Gold section marker */}
          <div className="flex items-center gap-3 justify-center mb-5">
            <span style={{ width: 32, height: 2, background: GOLD, display: "block" }} />
            <span className="text-xs font-semibold tracking-[0.25em] uppercase" style={{ color: GOLD }}>
              I-GROUP
            </span>
            <span style={{ width: 32, height: 2, background: GOLD, display: "block" }} />
          </div>

          <h1
            style={{
              fontFamily: '"Cormorant Garamond", Georgia, serif',
              fontSize: "clamp(2rem, 4vw, 3rem)",
              fontWeight: 600,
              color: NAVY,
              lineHeight: 1.15,
              marginBottom: 16,
            }}
          >
            {t.join_title}
          </h1>
          <p style={{ color: "#555", maxWidth: 560, margin: "0 auto", lineHeight: 1.65, fontSize: "0.95rem" }}>
            {t.join_subtitle}
          </p>
        </motion.div>

        {/* Card */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          style={{ border: `1px solid #e8e8e8`, boxShadow: "0 4px 32px rgba(11,48,142,0.07)" }}
          className="bg-white p-8 md:p-12"
        >
          {sent ? (
            /* Success state */
            <div className="text-center py-12">
              <div
                style={{
                  width: 64, height: 64,
                  background: `${GOLD}18`,
                  border: `2px solid ${GOLD}`,
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  margin: "0 auto 24px",
                }}
              >
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke={GOLD} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              </div>
              <h2 style={{ fontFamily: '"Cormorant Garamond", Georgia, serif', fontSize: "1.8rem", color: NAVY, fontWeight: 600, marginBottom: 12 }}>
                {t.join_sent_title}
              </h2>
              <p style={{ color: "#666", maxWidth: 420, margin: "0 auto 32px", lineHeight: 1.6 }}>
                {t.join_sent_msg}
              </p>
              <button
                onClick={() => navigate("/")}
                className="px-8 py-3 text-sm font-semibold text-white transition-colors"
                style={{ background: NAVY }}
              >
                Back to Home
              </button>
            </div>
          ) : (
            /* Form */
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Row: Surname + First Name */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <label className={labelClass} style={{ color: NAVY }}>
                    {t.join_field_surname}
                  </label>
                  <input
                    type="text"
                    required
                    value={form.surname}
                    onChange={(e) => setForm({ ...form, surname: e.target.value })}
                    placeholder={t.join_field_surname_ph}
                    className={inputClass}
                  />
                </div>
                <div>
                  <label className={labelClass} style={{ color: NAVY }}>
                    {t.join_field_name}
                  </label>
                  <input
                    type="text"
                    required
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder={t.join_field_name_ph}
                    className={inputClass}
                  />
                </div>
              </div>

              {/* Email */}
              <div>
                <label className={labelClass} style={{ color: NAVY }}>
                  {t.join_field_email}
                </label>
                <input
                  type="email"
                  required
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  placeholder={t.join_field_email_ph}
                  className={inputClass}
                />
              </div>

              {/* Country */}
              <div>
                <label className={labelClass} style={{ color: NAVY }}>
                  {t.join_field_country}
                </label>
                <select
                  required
                  value={form.country}
                  onChange={(e) => setForm({ ...form, country: e.target.value })}
                  className={inputClass}
                  style={{ cursor: "pointer" }}
                >
                  <option value="">{t.join_field_country_ph}</option>
                  {ALL_COUNTRIES.map((c) => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              </div>

              {/* Speciality */}
              <div>
                <label className={labelClass} style={{ color: NAVY }}>
                  {t.join_field_speciality}
                </label>
                <select
                  required
                  value={form.speciality}
                  onChange={(e) => setForm({ ...form, speciality: e.target.value })}
                  className={inputClass}
                  style={{ cursor: "pointer" }}
                >
                  <option value="">{t.join_field_speciality_ph}</option>
                  {SPECIALITIES.map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </div>

              {/* CV Upload */}
              <div>
                <label className={labelClass} style={{ color: NAVY }}>
                  {t.join_field_cv}
                </label>
                <div
                  style={{
                    border: `2px dashed ${GOLD}66`,
                    padding: "16px 20px",
                    cursor: "pointer",
                    background: cvFile ? `${GOLD}08` : "#fafafa",
                    transition: "background 0.2s",
                  }}
                >
                  <label style={{ cursor: "pointer", display: "flex", alignItems: "center", gap: 12 }}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={GOLD} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                      <polyline points="17 8 12 3 7 8" />
                      <line x1="12" y1="3" x2="12" y2="15" />
                    </svg>
                    <div>
                      <span className="text-sm font-medium" style={{ color: NAVY }}>
                        {cvFile ? cvFile.name : t.join_field_cv}
                      </span>
                      <p className="text-xs mt-0.5" style={{ color: "#888" }}>{t.join_field_cv_hint}</p>
                    </div>
                    <input
                      type="file"
                      accept=".pdf,.doc,.docx"
                      className="hidden"
                      onChange={(e) => setCvFile(e.target.files?.[0] || null)}
                    />
                  </label>
                </div>
              </div>

              {/* Error */}
              {error && (
                <div
                  style={{ background: "#fff0f0", border: "1px solid #fca5a5", padding: "12px 16px", color: "#b91c1c", fontSize: "0.875rem", borderRadius: 2 }}
                >
                  {error}
                </div>
              )}

              {/* Submit */}
              <button
                type="submit"
                disabled={submitting}
                className="w-full py-3.5 text-sm font-semibold text-white transition-all duration-200 disabled:opacity-60"
                style={{
                  background: submitting ? "#8a9fc5" : NAVY,
                  letterSpacing: "0.06em",
                }}
              >
                {submitting ? t.join_submitting : t.join_submit}
              </button>

              {/* Confidentiality note */}
              <p className="text-xs text-center" style={{ color: "#999" }}>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ display: "inline", marginRight: 4, verticalAlign: "middle" }}>
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                  <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                </svg>
                Your information is kept strictly confidential.
              </p>
            </form>
          )}
        </motion.div>

        {/* Decorative gold line bottom */}
        <div style={{ height: 2, background: `linear-gradient(90deg, transparent, ${GOLD}, transparent)`, marginTop: 48 }} />
      </div>
    </div>
  );
}
